import { isAxiosError } from "axios";
import { useEffect } from "react";
import { refreshProjectById } from "../features/projects/projectsSlice.js";
import { useAppDispatch } from "./hook.js";

const REFRESH_INTERVAL = 3000;

/**
 * Polls the project data by dispatching `refreshProjectById` at regular intervals
 * as long as the `refreshCondition` is true and within the maximum refresh count.
 */
export function useRefreshProjectWhile(
  projectId: string,
  refreshCondition: boolean,
  timeoutMs: number,
) {
  const maxRefreshCount = Math.ceil(timeoutMs / REFRESH_INTERVAL);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!refreshCondition) {
      return () => {};
    }

    let refreshCount = 0;
    const abortController = new AbortController();

    const scheduleRefreshProject = () => {
      if (refreshCount++ < maxRefreshCount && !abortController.signal.aborted) {
        setTimeout(refreshProject, REFRESH_INTERVAL);
      }
    };

    const refreshProject = async () => {
      try {
        await dispatch(
          refreshProjectById(projectId, {
            signal: abortController.signal,
          }),
        );
      } catch (error) {
        if (isAxiosError(error)) {
          abortController.abort();
        }

        throw error;
      } finally {
        scheduleRefreshProject();
      }
    };

    // Start the first refresh attempt
    scheduleRefreshProject();

    return () => {
      abortController.abort();
    };
  }, [projectId, refreshCondition, maxRefreshCount, dispatch]);
}
